import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listBrowserAiToolsDataTable,
  getBrowserAiToolsFilterOptions,
} from '@kirin/api-client/api/shadow-ai';
import type {
  BrowserAiToolListItem,
  BrowserAiToolsFilterOptionField,
  BrowserAiToolsSortField,
  BrowserAiNavigationType,
} from '@kirin/api-client/contracts/shadow-ai-browser-tools';
import { apiClient } from '@/lib/api-client';

/** KIR-3890 evidence-driven filters, threaded from the tab's toolbar state. */
export interface BrowserAiToolsEvidenceFilters {
  navigationType?: BrowserAiNavigationType;
  minVisits?: number;
}

export async function fetchBrowserAiTools(
  organizationId: string,
  params: DataFetchParams,
  signal?: AbortSignal,
  evidenceFilters?: BrowserAiToolsEvidenceFilters,
  scopeUserId?: string
): Promise<DataFetchResponse<BrowserAiToolListItem>> {
  const sortField = params.sort?.field as BrowserAiToolsSortField | undefined;
  const response = await listBrowserAiToolsDataTable(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: sortField,
      sortOrder: params.sort?.order,
      search: params.search,
      knownTotal: params.knownTotal,
      // Only send when a specific type is selected; omit for "All types".
      ...(evidenceFilters?.navigationType
        ? { navigationType: evidenceFilters.navigationType }
        : {}),
      ...(evidenceFilters?.minVisits && evidenceFilters.minVisits > 0
        ? { minVisits: evidenceFilters.minVisits }
        : {}),
      scopeUserId,
    },
    { signal }
  );

  return {
    data: response.data,
    pagination: response.pagination,
    meta: {
      activeFilters: response.meta?.activeFilters ?? [],
      search: response.meta?.search,
    },
  };
}

export async function fetchBrowserAiToolsFilterOptions(
  organizationId: string,
  field: BrowserAiToolsFilterOptionField,
  params: FilterOptionsParams,
  scopeUserId?: string
): Promise<DynamicFilterOptionsPage> {
  const response = await getBrowserAiToolsFilterOptions(
    apiClient,
    organizationId,
    {
      field,
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      scopeUserId,
    }
  );

  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}
